import { useEffect, useMemo, useState, useRef, useContext } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import StudentPanel from "./StudentPanel";
import type { PanelStudent } from "./StudentPanel";
import styles from "./StudentsInfoPanel.module.css"
import globalStyles from "../global.module.css"
import studentPanelStyles from "./StudentPanel.module.css"
import {  HighlightContext, TimePreferencesContext, completeDaysList } from "../App";
import type { StudentSchedulesEntry } from "../contexts/SchedulesCacheContext";

import { useUnsavedChanges } from "../contexts/UnsavedChangesContext";
import { useSchedulesCache } from "../contexts/SchedulesCacheContext";
import ScheduleTooltip from "../components/ScheduleTooltip";
import { debounce } from "../utils/debounce";
import LockLockedIcon from "../img/lock-keyhole-locked-svgrepo-com.svg";
import LockUnlockedIcon from "../img/lock-unlocked-svgrepo-com.svg";
import PenNewSquareIcon from "../img/pen-new-square-svgrepo-com.svg";

const DEBUG_HOVER = process.env.NODE_ENV === "development" && false;
const debugHover = (...args: unknown[]) => {
  if (DEBUG_HOVER) {
    console.log(...args);
  }
};

type StudentScheduleRow = StudentSchedulesEntry['schedules'][number];

interface TooltipPosition { x: number; y: number }

interface StudentsInfoPanelProps {
  schedule?: unknown;
  classId?: string | null;
  studentsList?: PanelStudent[] | null;
  classMembers?: unknown[] | null;
  onStudentsMerge?: unknown;
  onLockToggle?: ((memberId: string) => void) | null;
  /** Lehrerzeiten dieses Kurses – für den Schalter „Deine Zeiten im Raster“ (ersetzt TeacherTimesPanel). */
  teacherTimes?: Record<string, string[]> | null;
}

export default function StudentsInfoPanel({schedule, classId, studentsList, classMembers, onStudentsMerge, onLockToggle, teacherTimes}: StudentsInfoPanelProps) {

    const navigate = useNavigate();

    const toggleHighlightCells = useContext(HighlightContext);
    const { timePreferences } = useContext(TimePreferencesContext)

    const elementBelowCursorRef = useRef<Element | null>(null);

    const [loading, setLoading] = useState(true)
    const [error] = useState<string | null>(null)
    
    // *** Nutze den SchedulesCache statt lokalem State ***
    const { getSchedulesForStudent, loadSchedulesForStudent } = useSchedulesCache();

    // State für Tooltip
    const [tooltipState, setTooltipState] = useState<{
      visible: boolean;
      position: TooltipPosition | null;
      schedules: StudentScheduleRow[];
      studentMemberIds: string[];
    }>({
      visible: false,
      position: null,
      schedules: [],
      studentMemberIds: []
    });

    // State für Tooltip-Aktivierung
    const [tooltipEnabled, setTooltipEnabled] = useState(false);

    // State für Info-Tooltip
    const [infoTooltipState, setInfoTooltipState] = useState<{
      visible: boolean;
      position: TooltipPosition | null;
    }>({
      visible: false,
      position: null
    });

    const infoIconRef = useRef<HTMLSpanElement>(null);

    const { hasUnsavedChanges, requestConfirmation } = useUnsavedChanges();

    // States für Merge-Funktionalität
    // const [duplicateStudents, setDuplicateStudents] = useState({});
    // const [showMergeModal, setShowMergeModal] = useState(false);

    const panelRef = useRef<HTMLDivElement>(null);

    // „Deine Zeiten im Raster“: hält die Lehrerzeiten dauerhaft hervorgehoben (UX-Phase 3.4,
    // ersetzt das separate Namensfeld TeacherTimesPanel).
    const [showTeacherTimes, setShowTeacherTimes] = useState(false);
    useEffect(() => {
      if (!teacherTimes) return;
      toggleHighlightCells(teacherTimes as never, showTeacherTimes, globalStyles.teacherHighlight, '');
      return () => { toggleHighlightCells(teacherTimes as never, false, globalStyles.teacherHighlight, ''); };
    }, [showTeacherTimes, teacherTimes]); // eslint-disable-line react-hooks/exhaustive-deps

    // Nicht verplante zuerst – die brauchen Aufmerksamkeit
    const orderedStudents = useMemo(() => {
      if (!studentsList) return [];
      const isSet = (st: PanelStudent) => !!(st.is_set && st.set_day !== null && st.set_time_from_to && st.set_time_from_to.length >= 2);
      return {
        unassigned: studentsList.filter(st => !isSet(st)),
        assigned: studentsList.filter(st => isSet(st)),
      };
    }, [studentsList]);
    const hoveredMemberIdRef = useRef<string | null>(null); // Track currently hovered student to prevent stale async callbacks



    const handleNavigateToEditStudentsList = () => {
      const target = `/me/classes/teacher/${classId}`;
      if (!hasUnsavedChanges) {
        navigate(target);
      } else {
        requestConfirmation(() => navigate(target));
      }
    }

    const handleLockAll = () => {
      if (!studentsList || !onLockToggle) return;
      studentsList.forEach(student => {
        if (!student.is_locked) {
          onLockToggle(student.member_id);
        }
      });
    };

    const handleUnlockAll = () => {
      if (!studentsList || !onLockToggle) return;
      studentsList.forEach(student => {
        if (student.is_locked) {
          onLockToggle(student.member_id);
        }
      });
    };

    // Effekt zum Verarbeiten von classMembers (z.B. für Duplikate), ABER NICHT zum Setzen von studentsList
    useEffect(() => {
        const processMembers = async () => {
            if (!classMembers) {
              setLoading(false);
              console.log("StudentsInfoPanel: Keine classMembers");
              return;
            }

            if (classMembers) {
                console.log("StudentsInfoPanel: Verwende übergebene classMembers:", classMembers);
            } else {
                // Optional: Lade Mitglieder hier, wenn sie nicht übergeben werden
                // setLoading(true);
                // try {
                //     const response = await ApiClient.get(`classes/${classId}/members`);
                //     const data = await response.json();
                //     members = data.members || [];
                //     setLoading(false);
                // } catch (err) {
                //     setError("Fehler beim Laden der Mitglieder");
                //     setLoading(false);
                //     return;
                // }
                 setLoading(false); // Falls keine Mitglieder geladen werden
            }

            // Prüfe auf doppelte Studenten
            // const duplicates = findDuplicateStudents(members);
            // if (Object.keys(duplicates).length > 0) {
            //     console.log("StudentsInfoPanel: Doppelte Studenten gefunden:", duplicates);
            //     setDuplicateStudents(duplicates);
            //     setShowMergeModal(true);
            //     // Hier keine Mitglieder mehr filtern, das sollte idealerweise App machen oder handleMergeStudents
            // } else {
            //     setDuplicateStudents({}); // Stelle sicher, dass Duplikate geleert werden
            //     setShowMergeModal(false);
            // }
            setLoading(false); // Setze Loading auf false, nachdem Mitglieder verarbeitet wurden
        };

        processMembers();
    // Abhängigkeit nur von classId und classMembers
    }, [classId, classMembers]);

    // Effekt für loading state (verwendet jetzt abgeleitetes studentsList)
    useEffect(() => {
        if (studentsList !== null) {
             setLoading(false);
         } else if (!classId){
      setLoading(false);
         } else {
             setLoading(true);
         }
     }, [studentsList, classId]);


    useEffect(() => {

      const panelElement = panelRef.current;

      let timer: ReturnType<typeof setTimeout> | undefined;

      if (loading || !panelElement) {
        return undefined;
      }

      // Hilfsfunktion: Konvertiere Backend-Format zu Frontend-Format
      // Extrahiert nur Zeiten, in denen die member_ids des Studenten vorkommen
      // ONLY considers primary schedules - if no primary exists, no conflicts are shown
      const convertScheduleToConflictTimes = (
        schedules: StudentScheduleRow[],
        studentMemberIds: string[],
        currentClassId: string | null | undefined,
      ): Record<number, string[]> => {
        const conflictTimes: Record<number, string[]> = {};

        if (!studentMemberIds || studentMemberIds.length === 0) {
          return conflictTimes;
        }

        // Filtere Schedules: nur andere Klassen UND nur primäre Stundenpläne
        const otherClassesSchedules = schedules.filter(schedule =>
          schedule.class_id !== currentClassId && schedule.is_primary === true
        );
        
        otherClassesSchedules.forEach((currentSchedule) => {
          const availableTimes = currentSchedule.available_times || {};
          
          Object.entries(availableTimes as Record<string, unknown>).forEach(([dayIndex, timeSlots]) => {
            const parsedDayIndex = parseInt(dayIndex);
            const dayName = completeDaysList[parsedDayIndex];
            if (!dayName) {
              console.warn(`[convertScheduleToConflictTimes] Invalid day index: ${dayIndex}`);
              return;
            }
            
            if (!conflictTimes[parsedDayIndex]) {
              conflictTimes[parsedDayIndex] = [];
            }
            
            if (Array.isArray(timeSlots)) {
              timeSlots.forEach((slot: unknown) => {
                if (Array.isArray(slot) && slot.length >= 2) {
                  const time = slot[0];
                  const slotMemberId = slot[1];

                  // Nur hinzufügen, wenn die member_id des Slots zu unserem Studenten gehört
                  if (studentMemberIds.includes(slotMemberId)) {
                    if (time && !conflictTimes[parsedDayIndex].includes(time)) {
                      conflictTimes[parsedDayIndex].push(time);
                    }
                  }
                }
              });
            }
          });
        });
        
        debugHover("[StudentsInfoPanel convertScheduleToConflictTimes] conflictTimes", conflictTimes);
        return conflictTimes;
      };

      // *** Neue optimierte Funktion mit Cache-Lookup ***
      const loadStudentSchedules = async (studentUserId: string | null | undefined) => {
        if (!studentUserId) {
          return { schedules: [] as StudentScheduleRow[], member_ids: [] as string[] };
        }
        
        // Prüfe Cache zuerst
        const cachedResult = getSchedulesForStudent(studentUserId);
        if (cachedResult) {
          debugHover(`[StudentsInfoPanel loadStudentSchedules] Cache hit for ${studentUserId}`);
          return cachedResult;
        }
        
        // Fallback: Lade individuell wenn nicht im Cache
        debugHover(`[StudentsInfoPanel loadStudentSchedules] Cache miss for ${studentUserId}`);
        return await loadSchedulesForStudent(studentUserId);
      };

      const handleMouseOver = (event: MouseEvent) => {

        const target = event.target as Element;

        // make sure you're not targetting the StudentsInfoPanel - use class selector to find the correct parent
        const studentPanelElement = target.closest(`.${studentPanelStyles.studentPanel}`);
        if (!studentPanelElement) return

        

        timer = setTimeout(function() {
          const elementBelowCursor = elementBelowCursorRef.current;
          
          // Überprüfen, ob der Cursor immer noch über dem Element liegt
          if (target === elementBelowCursor) {
              // Funktion ausführen, falls der Cursor immer noch auf dem Element ist
              const memberId = studentPanelElement.id;
              hoveredMemberIdRef.current = memberId; // Track which student we're hovering
              const currentStudent = studentsList!.find(s => s.member_id === memberId);

              if (!currentStudent) {
                debugHover(`[StudentsInfoPanel handleMouseOver] No student found for ${memberId}`);
                return;
              }

              // if (!currentStudent.free_times) return;

              // Use timePreferences to determine which times to use
              if (!currentStudent.available_times && !currentStudent.available_times_user_input) {
                debugHover(`[StudentsInfoPanel handleMouseOver] No available times for ${memberId}`);
                return;
              }

              let availableTimesToUse: unknown = {};
              const preference = timePreferences?.[memberId];
              
              // Use the preference if set
              if (preference === 'available_times' && currentStudent.available_times) {
                availableTimesToUse = currentStudent.available_times;
              } else if (preference === 'available_times_user_input' && currentStudent.available_times_user_input) {
                availableTimesToUse = currentStudent.available_times_user_input;
              } 
              // Fallback to old logic if preference not set or preference not available
              else if (currentStudent.available_times) {
                availableTimesToUse = currentStudent.available_times;
              } else if (currentStudent.available_times_user_input) {
                availableTimesToUse = currentStudent.available_times_user_input;
              } else if (currentStudent.free_times) {
                availableTimesToUse = currentStudent.free_times;
              }

              toggleHighlightCells(availableTimesToUse, true, globalStyles.highlight, "");
              const favToHighlight = preference === 'available_times_user_input'
                ? currentStudent.favorite_times_user_input
                : currentStudent.favorite_times;
              if (favToHighlight) {
                toggleHighlightCells(favToHighlight, true, globalStyles.favoriteHighlight, "");
              }

              // Lade und markiere Konflikt-Zeiten (nur für registrierte Studenten)
              if (currentStudent.user_id) {
                loadStudentSchedules(currentStudent.user_id).then(result => {
                  // Check if we're still hovering over the same student (prevents stale async callbacks)
                  if (hoveredMemberIdRef.current !== memberId) {
                    debugHover(`[StudentsInfoPanel handleMouseOver] Stale callback for ${memberId}`);
                    return;
                  }

                  const schedules: StudentScheduleRow[] = result.schedules || [];
                  const studentMemberIds: string[] = result.member_ids || [];

                  if (schedules.length > 0 && studentMemberIds.length > 0) {
                    const conflictTimes = convertScheduleToConflictTimes(schedules, studentMemberIds, classId);
                    toggleHighlightCells(conflictTimes, true, globalStyles.scheduleConflict, "");
                    
                    // Zeige Tooltip nur wenn aktiviert
                    if (tooltipEnabled) {
                      setTooltipState({
                        visible: true,
                        position: { x: event.clientX, y: event.clientY },
                        schedules: schedules,
                        studentMemberIds: studentMemberIds
                      });
                    }
                  } else {
                    if (studentMemberIds.length === 0) {
                      console.warn(`[handleMouseOver] No member_ids received from API`);
                    }
                    setTooltipState({ visible: false, position: null, schedules: [], studentMemberIds: [] });
                  }
                }).catch((error: unknown) => {
                  console.error(`[handleMouseOver] Error in loadStudentSchedules promise:`, error);
                  setTooltipState({ visible: false, position: null, schedules: [], studentMemberIds: [] });
                });
              } else {
                debugHover(`[StudentsInfoPanel handleMouseOver] Student ${memberId} has no user_id`);
                setTooltipState({ visible: false, position: null, schedules: [], studentMemberIds: [] });
              }
          } else {
              // Abbrechen, falls der Cursor das Element verlassen hat
              return;
          }
      }, 300); // 300 ms Verzögerung

      };

      const handleMouseOut = (event: MouseEvent) => {
        clearTimeout(timer);
        hoveredMemberIdRef.current = null; // Clear hovered state to invalidate pending async callbacks
        const target = event.target as Element;

        const studentPanelElement = target.closest(`.${studentPanelStyles.studentPanel}`);
        if (studentPanelElement) {
          const memberId = studentPanelElement.id;
          const currentStudent = studentsList!.find(s => s.member_id === memberId);

          if (!currentStudent) {
            debugHover(`[StudentsInfoPanel handleMouseOut] Student not found for ${memberId}`);
            return;
          }

          if (!currentStudent.available_times && !currentStudent.available_times_user_input) {
            debugHover(`[StudentsInfoPanel handleMouseOut] No available times for ${memberId}`);
            return;
          }
          
          let availableTimesToUse: unknown = {};
          const preference = timePreferences?.[memberId];
          
          // Use the preference if set
          if (preference === 'available_times' && currentStudent.available_times) {
            availableTimesToUse = currentStudent.available_times;
          } else if (preference === 'available_times_user_input' && currentStudent.available_times_user_input) {
            availableTimesToUse = currentStudent.available_times_user_input;
          } 
          // Fallback to old logic if preference not set or preference not available
          else if (currentStudent.available_times) {
            availableTimesToUse = currentStudent.available_times;
          } else if (currentStudent.available_times_user_input) {
            availableTimesToUse = currentStudent.available_times_user_input;
          } else if (currentStudent.free_times) {
            availableTimesToUse = currentStudent.free_times;
          }

          toggleHighlightCells(availableTimesToUse, false, globalStyles.highlight, "");
          const favToUnhighlight = preference === 'available_times_user_input'
            ? currentStudent.favorite_times_user_input
            : currentStudent.favorite_times;
          if (favToUnhighlight) {
            toggleHighlightCells(favToUnhighlight, false, globalStyles.favoriteHighlight, "");
          }

          // Entferne auch Konflikt-Markierungen (nur für registrierte Studenten)
          if (currentStudent.user_id) {
            // *** Nutze Cache-Lookup statt lokalem State ***
            const cachedData = getSchedulesForStudent(currentStudent.user_id);
            const schedules = cachedData?.schedules || [];
            const studentMemberIds = cachedData?.member_ids || [];
            
            if (schedules.length > 0 && studentMemberIds.length > 0) {
              const conflictTimes = convertScheduleToConflictTimes(schedules, studentMemberIds, classId);
              toggleHighlightCells(conflictTimes, false, globalStyles.scheduleConflict, "");
            }
          } else {
            debugHover(`[StudentsInfoPanel handleMouseOut] Student ${memberId} has no user_id`);
          }
        }

        // Always hide tooltip on mouseout
        setTooltipState({ visible: false, position: null, schedules: [], studentMemberIds: [] });
      };


      const handleMouseMove = debounce((e: MouseEvent) => {
        elementBelowCursorRef.current = document.elementFromPoint(e.clientX, e.clientY);
        
        // Update Tooltip-Position wenn sichtbar
        if (tooltipState.visible) {
          setTooltipState(prev => ({
            ...prev,
            position: { x: e.clientX, y: e.clientY }
          }));
        }
      }, 50);


      if (panelElement) {
        panelElement.addEventListener('mouseover', handleMouseOver);
        panelElement.addEventListener('mouseout', handleMouseOut);
        panelElement.addEventListener('mousemove', handleMouseMove, {passive: true});
      }
  
      return () => {
        if (panelElement) {
          panelElement.removeEventListener('mouseover', handleMouseOver);
          panelElement.removeEventListener('mouseout', handleMouseOut);
          // passive ist nur eine Add-Option; beim Remove zählt allein capture (default)
          panelElement.removeEventListener('mousemove', handleMouseMove);
        }
      };

    }, [classId, classMembers, studentsList, loading, toggleHighlightCells, timePreferences, getSchedulesForStudent, loadSchedulesForStudent, tooltipEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading) {
      return <div className={styles.dockedPanel}>Lade Studenten...</div>;
    }
    
    if (error) {
      return <div className={styles.dockedPanel}>{error}</div>;
    }

    return (
        <>
        <div
      className={styles.dockedPanel}
      ref={panelRef}
    >
      {(!loading) && (
      <div className={styles.editStudentListButtonContainer}>
        <div className={styles.panelActionButtonsRow}>
          <button
          className={styles.editStudentListButton}
          onClick={handleNavigateToEditStudentsList}
          title="Studentenliste bearbeiten"
          aria-label="Studentenliste bearbeiten"
          >
            <img src={PenNewSquareIcon} alt="" aria-hidden="true" className={styles.panelActionIcon} />
          </button>
          {onLockToggle && studentsList && studentsList.length > 0 && (
            <div className={styles.iconOnlyLockButtonsContainer}>
              <button
                className={styles.iconOnlyLockButton}
                onClick={handleLockAll}
                title="Alle eingetragenen Studierenden fixieren"
                aria-label="Alle eingetragenen Studierenden fixieren"
              >
                <img src={LockLockedIcon} alt="" aria-hidden="true" className={styles.panelActionIcon} />
              </button>
              <button
                className={styles.iconOnlyLockButton}
                onClick={handleUnlockAll}
                title="Alle fixierten Studierenden freigeben"
                aria-label="Alle fixierten Studierenden freigeben"
              >
                <img src={LockUnlockedIcon} alt="" aria-hidden="true" className={styles.panelActionIcon} />
              </button>
            </div>
          )}
        </div>
        <div className={styles.tooltipCheckboxContainer}>
          <label className={styles.tooltipCheckboxLabel}>
            <input
              type="checkbox"
              checked={tooltipEnabled}
              onChange={(e) => setTooltipEnabled(e.target.checked)}
              className={styles.tooltipCheckbox}
            />
            <span>Konflikt-Tooltip</span>
            <span 
              className={styles.infoIcon}
              ref={infoIconRef}
              onMouseEnter={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setInfoTooltipState({
                  visible: true,
                  position: { x: rect.right, y: rect.top + rect.height / 2 }
                });
              }}
              onMouseLeave={() => {
                setInfoTooltipState({ visible: false, position: null });
              }}
            >
              ℹ️
            </span>
          </label>
        </div>
      </div>)}
      {!loading && teacherTimes && (
        <label className={styles.teacherTimesToggle}>
          <input type="checkbox" checked={showTeacherTimes} onChange={(e) => setShowTeacherTimes(e.target.checked)} />
          <span>Deine Zeiten im Raster zeigen</span>
        </label>
      )}
      {!loading && studentsList?.some(st => st.available_times && st.available_times_user_input) && (
        <p className={styles.timeSourceLegend}>
          <strong>Lehrkraft</strong> = von dir bearbeitete Zeiten · <strong>Selbst</strong> = von der Person eingetragen
        </p>
      )}
      {studentsList && studentsList.length > 0 && !Array.isArray(orderedStudents) ? (
        // Nur dieser Bereich scrollt — Knöpfe und Häkchen oben bleiben stehen
        <div className={styles.studentScroll}>
          {orderedStudents.unassigned.length > 0 && (
            <div className={styles.groupLabel}>Nicht verplant ({orderedStudents.unassigned.length})</div>
          )}
          {orderedStudents.unassigned.map((student) => (
            <StudentPanel
              key={`${student.name}-${student.member_id}`}
              studentName={student.name}
              studentMemberId={student.member_id}
              student={student}
            />
          ))}
          {orderedStudents.assigned.length > 0 && (
            <div className={styles.groupLabel}>Verplant ({orderedStudents.assigned.length})</div>
          )}
          {orderedStudents.assigned.map((student) => (
            <StudentPanel
              key={`${student.name}-${student.member_id}`}
              studentName={student.name}
              studentMemberId={student.member_id}
              student={student}
            />
          ))}
        </div>
      ) : (
        <div className={styles.noStudentsContainer}>
          <span className={styles.noStudents}>Keine Studenten für diesen Kurs gefunden oder Daten werden geladen.</span>
        </div>
      )}
    </div>
        
        {/* Merge-Dialog für doppelte Studenten */}
        {/* <MergeStudentsModal 
          isOpen={showMergeModal}
          onClose={handleCloseMergeModal}
          onMerge={handleTriggerMerge}
          duplicateStudents={duplicateStudents}
        /> */}
        {tooltipEnabled && tooltipState.visible && tooltipState.position && (
          <ScheduleTooltip
            schedules={tooltipState.schedules}
            studentMemberIds={tooltipState.studentMemberIds}
            position={tooltipState.position}
            currentClassId={classId}
          />
        )}
        {infoTooltipState.visible && infoTooltipState.position && createPortal(
          <div
            className={styles.infoTooltip}
            style={{
              left: `${infoTooltipState.position.x + 10}px`,
              top: `${infoTooltipState.position.y}px`,
              transform: 'translateY(-50%)'
            }}
          >
            Zeigt beim Hovern über Studenten einen Tooltip mit Informationen über bereits vergebene Zeiten in anderen Kursen an.
          </div>,
          document.body
        )}
        </>
    );
}
